import { z } from 'zod';
// --- Key type registry (Single Source of Truth) ---
export const API_KEY_TYPES = ['client', 'server'];
// --- Role registry (Single Source of Truth) ---
export const ADMIN_ROLES = ['admin', 'viewer'];
// --- Slug validation ---
export const projectSlugSchema = z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase alphanumeric with hyphens, no leading/trailing hyphen')
    .meta({
    description: 'URL-safe project slug (lowercase kebab-case)',
    example: 'orderly-peptides',
});
// --- Create project request ---
export const createProjectRequestSchema = z
    .object({
    name: z
        .string()
        .min(1)
        .max(200)
        .meta({ description: 'Human-readable project name', example: 'Orderly Peptides' }),
    slug: projectSlugSchema,
    allowed_origins: z
        .array(z.string().max(500))
        .max(50)
        .meta({
        description: 'Domains allowed to send events (supports wildcards)',
        example: ['orderlypeptides.com', '*.orderlypeptides.com'],
    }),
})
    .meta({ id: 'CreateProjectRequest', description: 'Create a new project' });
// --- Update project request ---
export const updateProjectRequestSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    allowed_origins: z.array(z.string().max(500)).max(50).optional(),
})
    .meta({
    id: 'UpdateProjectRequest',
    description: 'Update an existing project (all fields optional)',
});
// --- Project document / response (Firestore shape = API response shape) ---
const projectShapeSchema = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    allowed_origins: z.array(z.string()),
    archived: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
    created_by: z.string(),
});
/**
 * Zod schema for the project Firestore document. Not registered as an OpenAPI
 * component (no meta.id) — use projectResponseSchema for API responses.
 */
export const projectDocSchema = projectShapeSchema;
/**
 * Zod schema for the ProjectResponse OpenAPI component.
 * Structurally identical to projectDocSchema.
 */
export const projectResponseSchema = projectShapeSchema.meta({
    id: 'ProjectResponse',
    description: 'Project details',
});
// --- Project creation response (includes initial keys) ---
export const projectCreatedResponseSchema = projectShapeSchema
    .extend({
    client_key: z
        .string()
        .meta({ description: 'Initial client API key (shown once)', example: 'om_pub_abc123' }),
    server_key: z
        .string()
        .meta({ description: 'Initial server API key (shown once)', example: 'om_sec_xyz789' }),
})
    .meta({ id: 'ProjectCreatedResponse', description: 'Project with initial API keys' });
// --- API key document / response (Firestore shape = API response shape) ---
const apiKeyShapeSchema = z.object({
    id: z.string(),
    project_id: z.string(),
    type: z.enum(API_KEY_TYPES),
    active: z.boolean(),
    created_at: z.string(),
    created_by: z.string(),
    key_preview: z.string().meta({ description: 'Masked key preview', example: 'om_pub_***abc' }),
});
/**
 * Zod schema for the API key Firestore document. Not registered as an OpenAPI
 * component (no meta.id) — use apiKeyResponseSchema for API responses.
 */
export const apiKeyDocSchema = apiKeyShapeSchema;
/**
 * Zod schema for the ApiKeyResponse OpenAPI component.
 * Structurally identical to apiKeyDocSchema.
 */
export const apiKeyResponseSchema = apiKeyShapeSchema.meta({
    id: 'ApiKeyResponse',
    description: 'API key metadata (key value is never shown again)',
});
// --- Generated API key response (includes full key, shown once) ---
export const generatedApiKeyResponseSchema = apiKeyShapeSchema
    .extend({
    key: z
        .string()
        .meta({ description: 'Full API key (shown only on creation)', example: 'om_pub_abc123' }),
})
    .meta({ id: 'GeneratedApiKeyResponse', description: 'Newly generated API key with full value' });
// --- Generate key request ---
export const generateKeyRequestSchema = z
    .object({
    type: z.enum(API_KEY_TYPES).meta({
        description: 'Key type: client (public, browser-safe) or server (secret, server-only)',
    }),
})
    .meta({ id: 'GenerateKeyRequest', description: 'Generate a new API key for the project' });
//# sourceMappingURL=projects.js.map